/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import styles from './styles.module.scss';
import { Actionable, Button, Divider, Text, View } from '@az/starc-ui';
import { SubmitFeedbackContextProvider } from '@/features/reviews';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { useState, useMemo, useEffect } from 'react';
import { ReviewsOverview } from '../ReviewsOverview/ReviewsOverview';
import { ReviewSummary } from '../ReviewSummary/ReviewSummary';
import { useGetReviewsInfinite } from '@/features/customReviews/api/getReviews';
import ReviewsFilter from '@/features/customReviews/components/ReviewsFilter';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { Header } from '../Header/Header';
import { useLabels } from '@/hooks/useLabels';
import { PreviewImages } from '../ReviewImages/PreviewImages/PreviewImages';
import { useProductReviewImages } from '../../hooks/useProductReviewImages';
import { useReviewFilters } from '../../context/ReviewFiltersProvider';
import { ReviewsList } from '../ReviewsList/ReviewsList';
import { NoReviews } from '../NoReviews/NoReviews';
import { scrollToCustomerReviews } from '../../utils/scrollToTitle';
import { CustomReviewsMetaData } from '../CustomReviewsMetaData/CustomReviewsMetaData';
import { useBvPixelTracking } from '../../hooks/useBvPixelTracking';
import { BazaarVoicePixel } from '../BazaarVoicePixel';
import { PRPLink } from '../PRPLink/PRPLink';
import { useReviewStatistics } from '../../api/getReviewStatistics';
import type { SortAttributeType } from '../../interface/types';
import { DEFAULT_SORT_ATTRIBUTE } from '../../constants/defaultSortAttribute';
import { useProductReviews } from '@/features/reviews/api/getProductReviews';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useIsBot } from '@/utils/useIsBot';
import { useLocale } from '@/hooks/useLocale';
import { MIN_PRP_REVIEW_COUNT } from '@/features/reviews/constants';

const labelMap = {
  customerReviews: 'label_reviews_customerReviews',
  loadMore: 'label_load_more',
  loading: 'label_reviews_loading',
  backToTop: 'label_back_to_top',
};

type CustomReviewsProps = {
  showHeader?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  onContentLoaded?: () => void;
};

export function CustomReviews({
  showHeader = true,
  containerRef,
  onContentLoaded,
}: CustomReviewsProps) {
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const isBot = useIsBot();
  const { selectedRatings } = useReviewFilters();

  const [isVerified, setIsVerified] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState<string | undefined>(undefined);
  const [sortAttribute, setSortAttribute] = useState<SortAttributeType>(DEFAULT_SORT_ATTRIBUTE);
  const [searchTerm, setSearchTerm] = useState('');
  const { data: productDetailsData } = useProductDetails();
  const product = productDetailsData?.product;
  const skuNumber = product?.itemId ?? '';

  const reviewImageResult = useProductReviewImages();
  const starRatingParams = useMemo(
    () => ({
      ...Object.fromEntries(selectedRatings.map((r) => [`showReviewsRated${r}Star`, true])),
      ...(isVerified && { showReviewsFromVerifiedPurchasers: true }),
      ...(!!selectedKeyword && { feature: selectedKeyword }),
    }),
    [selectedRatings, isVerified, selectedKeyword],
  );
  const reviewMediaItems = reviewImageResult?.mediaItems ?? [];
  const reviewImageReviews = reviewImageResult?.reviews;

  const mediaCount = reviewMediaItems.length;
  const {
    data: reviewsData,
    totalReviews,
    fetchNextPage,
    isFetching,
    isPending: reviewsDataIsPending,
    isLoading: reviewsDataIsLoading,
    hasNextPage,
  } = useGetReviewsInfinite({
    skuNumber,
    sortAttribute,
    limit: 20,
    searchText: searchTerm || undefined,
    ...starRatingParams,
  });

  const { data: reviewStatisticsData, isLoading: reviewStatisticIsLoading } = useReviewStatistics({
    skuNumbers: [skuNumber],
    enabled: Boolean(skuNumber),
  });

  const { data: seoProductReviews } = useProductReviews({
    country: getCountryFromLocale(locale),
    customerType: 'B2C',
    skuId: skuNumber,
    enabled: !!skuNumber,
    pageNumber: isBot ? 0 : 1,
  });

  const reviewStatistic = reviewStatisticsData?.[skuNumber];

  const totalReviewCount = Number(reviewStatistic?.totalReviewCount ?? 0);

  const hasReviews = reviewsDataIsLoading || reviewStatisticIsLoading || totalReviewCount > 0;

  const { trackEvent } = useBvPixelTracking();

  const handleSortChange = (value: string | object) => {
    if (typeof value === 'string') {
      setSortAttribute(value as SortAttributeType);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleLoadMore = () => {
    if (!isFetching) {
      void fetchNextPage();
      trackEvent('Customer Reviews', 'Click', 'Load More');
    }
  };

  const handleKeywordChange = (keyword: string) => {
    if (selectedKeyword === keyword) {
      setSelectedKeyword(undefined);
    } else {
      setSelectedKeyword(keyword);
    }
  };

  // Notify parent when initial content has loaded for dynamic resizing
  useEffect(() => {
    if (!reviewsDataIsPending && !reviewStatisticIsLoading && onContentLoaded) {
      onContentLoaded();
    }
  }, [reviewsDataIsPending, reviewStatisticIsLoading, onContentLoaded]);

  // Trigger recalculation when reviews data changes (e.g., after Load More)
  useEffect(() => {
    if (onContentLoaded && !reviewsDataIsPending) {
      // Use RAF to ensure DOM has updated
      const rafId = requestAnimationFrame(() => {
        onContentLoaded();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [reviewsData, onContentLoaded, reviewsDataIsPending]);

  const showLoadMore = totalReviews !== 0 && hasNextPage;
  const showBackToTop = totalReviews > 0;
  const showBottomButtons = showLoadMore || showBackToTop;

  return (
    <div id="custom-reviews-container" ref={containerRef}>
      <View padding={{ s: !showHeader ? 0 : 4, m: 0 }}>
        <CustomReviewsMetaData product={product} productReviews={seoProductReviews} />
        <SubmitFeedbackContextProvider>
          <FlexGrid flexDirection="column" gap={{ sm: 6, md: 5 }}>
            {showHeader && <Header />}
            {hasReviews ? (
              <>
                <ReviewsOverview />
                {mediaCount > 0 && (
                  <PreviewImages
                    mediaItems={reviewMediaItems}
                    reviews={reviewImageReviews}
                    photoMap={reviewImageResult?.photoMap}
                  />
                )}
                <FlexGrid flexDirection="column" gap={{ md: 5 }}>
                  <ReviewSummary />
                  <Divider className={styles.preFilterDivider} />
                  <ReviewsFilter
                    onVerifiedFilter={setIsVerified}
                    onSortChange={handleSortChange}
                    onSearchChange={handleSearchChange}
                    onKeywordSelect={handleKeywordChange}
                    initialSelectedFilters={[]}
                    initialVerifiedSelected={isVerified}
                    totalReviews={totalReviews}
                    selectedKeyword={selectedKeyword}
                  />
                  {reviewsData.length > 0 || reviewsDataIsPending ? (
                    <ReviewsList
                      reviews={reviewsData}
                      isLoading={reviewsDataIsPending}
                      selectedKeyword={selectedKeyword}
                    />
                  ) : null}
                  {showBottomButtons ? (
                    <View className={styles.buttonsContainer}>
                      {showLoadMore ? (
                        <>
                          <View width={{ s: '100px', m: '150px' }}>
                            <Button
                              className={styles.loadMore}
                              onClick={handleLoadMore}
                              loading={isFetching}
                            >
                              {labels.loadMore}
                            </Button>
                          </View>
                          <View padding={3} />
                        </>
                      ) : null}
                      {showBackToTop ? (
                        <View height={'42px'} justify={'center'}>
                          <Actionable
                            onClick={scrollToCustomerReviews}
                            attributes={{ 'data-testid': 'backToTopButton' }}
                          >
                            <Text variant="text-link" decoration="underline" weight="medium">
                              {labels.backToTop}
                            </Text>
                          </Actionable>
                        </View>
                      ) : null}
                    </View>
                  ) : null}
                  {seoProductReviews &&
                  seoProductReviews.totalReviewCount > MIN_PRP_REVIEW_COUNT ? (
                    <PRPLink />
                  ) : null}
                </FlexGrid>
              </>
            ) : (
              <>
                <NoReviews productId={skuNumber} />
              </>
            )}
          </FlexGrid>
        </SubmitFeedbackContextProvider>
        <BazaarVoicePixel />
      </View>
    </div>
  );
}
